import { useState } from 'react';
import { useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { signIn, signUp } from '../../services/accountApi';
import { useAuth } from '../../context/AuthContext';
import { trackLogin, trackSignUp } from '../../utils/analytics';
import '../../styles/Account.css';
import { friendlyError } from '../../utils/errors';

export default function AccountLogin() {
  const [mode, setMode] = useState('login'); // 'login' | 'signup'
  const [form, setForm] = useState({
    email: '',
    password: '',
    fullName: '',
    phone: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();

  const redirectTo = location.state?.from || '/account';

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');

    if (!form.email || !form.password) {
      return setError('Email and password are required.');
    }
    if (mode === 'signup' && form.password.length < 8) {
      return setError('Password must be at least 8 characters.');
    }
    if (mode === 'signup' && !form.fullName.trim()) {
      return setError('Please enter your name.');
    }

    setBusy(true);

    if (mode === 'login') {
      const { error } = await signIn({
        email: form.email,
        password: form.password,
      });
      setBusy(false);

      if (error) return setError(friendlyError(error));
      trackLogin();
      navigate(redirectTo, { replace: true });
      return;
    }

    const { data, error } = await signUp({
      email: form.email,
      password: form.password,
      fullName: form.fullName,
      phone: form.phone,
    });
    setBusy(false);

    if (error) return setError(friendlyError(error));

    trackSignUp();

    // With email confirmation on, there's no session until they verify.
    if (!data.session) {
      setNotice(
        'Account created. Check your email for a confirmation link, then sign in.'
      );
      setMode('login');
      return;
    }

    navigate('/account', { replace: true });
  };

  // Already signed in — no reason to show the form.
  if (!loading && user) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <section className="account-auth-page">
      <motion.div
        className="account-auth-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h1>{mode === 'login' ? 'Sign In' : 'Create Account'}</h1>
        <p className="auth-sub">
          {mode === 'login'
            ? 'Access your orders, consultations and gemstone profile.'
            : 'Track your orders and get personalised gemstone guidance.'}
        </p>

        <div className="auth-tabs">
          <button
            type="button"
            className={mode === 'login' ? 'active' : ''}
            onClick={() => {
              setMode('login');
              setError('');
            }}
          >
            Sign In
          </button>
          <button
            type="button"
            className={mode === 'signup' ? 'active' : ''}
            onClick={() => {
              setMode('signup');
              setError('');
            }}
          >
            Register
          </button>
        </div>

        <form onSubmit={handleSubmit} className="account-form">
          {mode === 'signup' && (
            <>
              <label>
                Full Name
                <input
                  value={form.fullName}
                  onChange={(e) => set('fullName', e.target.value)}
                  placeholder="Your name"
                />
              </label>
              <label>
                Phone (WhatsApp)
                <input
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  placeholder="+91 99999 00000"
                />
                <span className="field-hint">
                  Used to link any orders you already placed over WhatsApp.
                </span>
              </label>
            </>
          )}

          <label>
            Email
            <input
              type="email"
              value={form.email}
              autoComplete="email"
              onChange={(e) => set('email', e.target.value)}
            />
          </label>

          <label>
            Password
            <input
              type="password"
              value={form.password}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              onChange={(e) => set('password', e.target.value)}
              placeholder={mode === 'signup' ? 'At least 8 characters' : ''}
            />
          </label>

          {error && <p className="account-error">{error}</p>}
          {notice && <p className="account-success">{notice}</p>}

          <button type="submit" className="account-btn" disabled={busy}>
            {busy
              ? 'Please wait…'
              : mode === 'login'
              ? 'Sign In'
              : 'Create Account'}
          </button>
        </form>

        <p className="auth-foot">
          <Link to="/">← Back to store</Link>
        </p>
      </motion.div>
    </section>
  );
}
